import { FormFieldConfig } from '@shared/components/search-form/search-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import {
  FormField,
  ActionButton,
  SectionButton,
} from '@shared/components/add-form/add-form.component';
import { Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ListOfTreeVO } from '@app/api/channel-service/model/listOfTreeVO';
import { alphanumericValidator } from '../../pos/pos-profile/pos-profile.config';

// ============================================================================
// INTERFACES
// ============================================================================

export interface RetailStoreItem {
  id: string;
  name: string;
  code: string;
  typeCode: string;
  address: string;
  manager: string;
  status: boolean;
  salesOrganizingId: number;
  invoiceTypeCode: string;
  createdBy: string;
  creationDate: string;
}

export interface RetailStoreListResponse {
  data: RetailStoreItem[];
  totalRecords: number;
  loading: boolean;
}

export interface RetailStoreFormConfig {
  title: string;
  formFields: FormField[];
  headerActions: ActionButton[];
  sectionButtons?: SectionButton[];
  listRoute: string;
  companyOptions?: Array<{ label: string; value: number }>;
  warehouseOptions?: Array<{ label: string; value: string }>;
}

export type RetailStoreAction = 'view' | 'edit' | 'delete' | 'disable' | 'add';

export interface RetailStoreFormActionEvent {
  action: string;
  data?: any;
}

// ============================================================================
// FORM FIELD OPTIONS
// ============================================================================

export const STATUS_OPTIONS = [
  { label: 'common.table.status.active', value: true },
  { label: 'common.table.status.inactive', value: false },
];

// ============================================================================
// SEARCH FORM CONFIGURATIONS
// ============================================================================

export const RETAIL_STORE_SEARCH_CONFIG: FormFieldConfig[] = [
  {
    key: 'name',
    label: 'retailStore.search.fields.store_name',
    type: 'text',
    placeholder: 'retailStore.search.placeholders.enter_store_name',
  },
  {
    key: 'manager',
    label: 'retailStore.search.fields.manager',
    type: 'text',
    placeholder: 'retailStore.search.placeholders.enter_manager',
  },
  // {
  //   key: 'typeCode',
  //   label: 'retailStore.search.fields.store_type',
  //   type: 'select',
  //   options: STORE_TYPE_OPTIONS,
  //   placeholder: 'retailStore.search.placeholders.select_store_type',
  // },
  // {
  //   key: 'channelWarehouseStatus',
  //   label: 'retailStore.search.fields.channel_warehouse_status',
  //   type: 'select',
  //   options: STATUS_OPTIONS,
  //   placeholder: 'retailStore.search.placeholders.select_channel_warehouse_status',
  // },
  {
    key: 'status',
    label: 'retailStore.search.fields.status',
    type: 'select',
    options: STATUS_OPTIONS,
    placeholder: 'retailStore.search.placeholders.select_status',
  },
  {
    key: 'selfPickupFlag',
    label: 'retailStore.search.fields.self_pickup',
    type: 'select',
    options: [
      { label: 'common.table.status.yes', value: true },
      { label: 'common.table.status.no', value: false },
    ],
    placeholder: 'retailStore.search.placeholders.select_self_pickup',
  },
];

// ============================================================================
// TABLE COLUMN CONFIGURATIONS
// ============================================================================

/**
 * Default column property names (shown on first load)
 * Update these constants to change column property names
 */
export const RETAIL_STORE_DEFAULT_COLUMNS = {
  CODE: 'code',
  NAME: 'name',
  TYPE_CODE: 'typeCode',
  MANAGER: 'manager',
  ADDRESS: 'address',
  STATUS: 'status',
  CHANNEL_WAREHOUSE_NAME: 'channelWarehouseName',
  SELF_PICKUP_FLAG: 'selfPickupFlag',
} as const;

/**
 * Flexible column property names (can be shown/hidden via column selector)
 * Update these constants to change flexible column property names
 */
export const RETAIL_STORE_FLEXIBLE_COLUMNS = {
  CREATION_DATE: 'creationDate',
} as const;

/**
 * Action column property name
 */
export const RETAIL_STORE_ACTION_COLUMN = {
  ACTIONS: 'actions',
} as const;

/**
 * Default columns order (always visible)
 * Update this array to change the default column order
 */
export const RETAIL_STORE_DEFAULT_COLUMNS_ORDER: string[] = [
  RETAIL_STORE_DEFAULT_COLUMNS.CODE,
  RETAIL_STORE_DEFAULT_COLUMNS.NAME,
  RETAIL_STORE_DEFAULT_COLUMNS.TYPE_CODE,
  RETAIL_STORE_DEFAULT_COLUMNS.MANAGER,
  RETAIL_STORE_DEFAULT_COLUMNS.ADDRESS,
  RETAIL_STORE_DEFAULT_COLUMNS.STATUS,
  RETAIL_STORE_DEFAULT_COLUMNS.CHANNEL_WAREHOUSE_NAME,
  RETAIL_STORE_DEFAULT_COLUMNS.SELF_PICKUP_FLAG,
];

/**
 * Flexible columns array (can be shown/hidden via column selector)
 * Update this array to change which columns are flexible
 */
export const RETAIL_STORE_FLEXIBLE_COLUMNS_ORDER: string[] = [
  RETAIL_STORE_FLEXIBLE_COLUMNS.CREATION_DATE,
];

export const RETAIL_STORE_COLUMNS: TableColumn[] = [
  {
    label: 'retailStore.table.columns.store_code',
    prop: RETAIL_STORE_DEFAULT_COLUMNS.CODE,
    width: 120,
  },
  {
    label: 'retailStore.table.columns.store_name',
    prop: RETAIL_STORE_DEFAULT_COLUMNS.NAME,
    width: 160,
  },
  {
    label: 'retailStore.table.columns.store_type',
    prop: RETAIL_STORE_DEFAULT_COLUMNS.TYPE_CODE,
    width: 100,
    type: 'custom',
    template: 'storeTypeTemplate',
  },
  {
    label: 'retailStore.table.columns.manager',
    prop: RETAIL_STORE_DEFAULT_COLUMNS.MANAGER,
    width: 120,
  },
  {
    label: 'retailStore.table.columns.address',
    prop: RETAIL_STORE_DEFAULT_COLUMNS.ADDRESS,
    width: 200,
  },
  {
    label: 'retailStore.table.columns.store_status',
    prop: RETAIL_STORE_DEFAULT_COLUMNS.STATUS,
    width: 100,
    type: 'status',
  },
  {
    label: 'retailStore.table.columns.channel_warehouse_name',
    prop: RETAIL_STORE_DEFAULT_COLUMNS.CHANNEL_WAREHOUSE_NAME,
    width: 180,
  },
  // { label: 'retailStore.table.columns.channel_warehouse_status', prop: 'channelWarehouseStatus', width: 160, type: 'status' },
  {
    label: 'retailStore.table.columns.self_pickup',
    prop: RETAIL_STORE_DEFAULT_COLUMNS.SELF_PICKUP_FLAG,
    width: 100,
    type: 'custom',
    template: 'selfPickupTemplate',
  },
  {
    label: 'retailStore.table.columns.created_date',
    prop: RETAIL_STORE_FLEXIBLE_COLUMNS.CREATION_DATE,
    width: 135,
    type: 'custom',
    template: 'dateTemplate',
  },
  {
    label: 'retailStore.table.columns.actions',
    prop: RETAIL_STORE_ACTION_COLUMN.ACTIONS,
    width: 240,
    type: 'actions',
  },
];

// ============================================================================
// ADD/EDIT FORM CONFIGURATIONS
// ============================================================================

export const RETAIL_STORE_FORM_CONFIG: RetailStoreFormConfig = {
  title: 'retailStore.form.add_physical_store',
  listRoute: '/channel/retail-store',
  sectionButtons: [
    {
      label: 'retailStore.form.actions.create_store_warehouse',
      action: 'create_store_warehouse',
      severity: 'info',
      outlined: false,
      icon: 'add',
      section: 'retailStore.form.sections.store_billing',
    },
  ],
  formFields: [
    // 1. Store Info (Basic Information)
    {
      label: 'retailStore.form.fields.store_name',
      controlName: 'name',
      type: 'text',
      placeholder: 'retailStore.form.placeholders.enter_store_name',
      required: true,
      section: 'retailStore.form.sections.store_info',
      validators: [Validators.required, Validators.maxLength(100)],
      errorMessages: {
        required: 'retailStore.form.validation.store_name_required',
        maxlength: 'common.validation.maxlength',
      },
    },
    {
      label: 'retailStore.form.fields.store_code',
      controlName: 'code',
      type: 'text',
      placeholder: 'retailStore.form.placeholders.enter_store_code',
      required: true,
      section: 'retailStore.form.sections.store_info',
      validators: [
        Validators.required,
        Validators.maxLength(50),
        alphanumericValidator,
      ],
      errorMessages: {
        required: 'retailStore.form.validation.store_code_required',
        maxlength: 'common.validation.maxlength',
        alphanumeric: 'common.validation.code_alphanumeric',
      },
    },
    {
      label: 'retailStore.form.fields.store_type',
      controlName: 'typeCode',
      type: 'text',
      defaultValue: 'retailStore.types.general_shop', // Use actual translated value instead of key
      readonly: true,
      disabled: true,
      section: 'retailStore.form.sections.store_info',
    },
    {
      label: 'retailStore.form.fields.sales_organization_id',
      controlName: 'salesOrganizingId',
      type: 'dropdown',
      placeholder: 'retailStore.form.placeholders.select_sales_organization',
      required: true,
      section: 'retailStore.form.sections.store_info',
      validators: [Validators.required],
      errorMessages: {
        required: 'retailStore.form.validation.sales_organization_id_required',
      },
      // Options will be populated dynamically
      options: [],
    },
    {
      label: 'retailStore.form.fields.store_manager',
      controlName: 'manager',
      type: 'text',
      placeholder: 'retailStore.form.placeholders.enter_store_manager',
      required: true,
      section: 'retailStore.form.sections.store_info',
      validators: [Validators.required, Validators.maxLength(100)],
      errorMessages: {
        required: 'retailStore.form.validation.store_manager_required',
        maxlength: 'common.validation.maxlength',
      },
    },
    {
      label: 'retailStore.form.fields.description',
      controlName: 'description',
      type: 'text',
      placeholder: 'retailStore.form.placeholders.enter_description',
      section: 'retailStore.form.sections.store_info',
      validators: [Validators.maxLength(500)],
      errorMessages: {
        maxlength: 'common.validation.maxlength',
      },
    },
    {
      label: 'retailStore.form.fields.has_shipper',
      controlName: 'hasShipper',
      type: 'checkbox',
      section: 'retailStore.form.sections.store_info',
      defaultValue: false,
    },
    {
      label: 'retailStore.form.fields.store_address',
      controlName: 'address',
      type: 'text',
      required: true,
      placeholder: 'retailStore.form.placeholders.enter_store_address',
      section: 'retailStore.form.sections.store_address',
      validators: [Validators.maxLength(500)],
      errorMessages: {
        maxlength: 'common.validation.maxlength',
        required: 'retailStore.form.validation.store_address_required',
      },
    },
    // Country, Province, City, County
    {
      label: 'retailStore.form.fields.province_id',
      controlName: 'provinceId',
      required: true,
      type: 'searchableDropdown',
      placeholder: 'retailStore.form.placeholders.select_province',
      section: 'retailStore.form.sections.store_address',
      validators: [Validators.required],
      errorMessages: {
        required: 'retailStore.form.validation.province_id_required',
      },
      options: [],
    },
    {
      label: 'retailStore.form.fields.channel_warehouse_name',
      controlName: 'channelWarehouseName',
      type: 'dropdown',
      required: true,
      placeholder: 'retailStore.form.placeholders.select_logical_warehouse',
      section: 'retailStore.form.sections.store_billing',
      validators: [Validators.required],
      errorMessages: {
        required: 'retailStore.form.validation.warehouse_name_required',
      },
      // Options will be populated dynamically
      options: [],
    },
    {
      label: 'retailStore.form.fields.billing_type',
      controlName: 'invoiceTypeCode',
      type: 'text',
      defaultValue: 'retailStore.invoice_types.vat_invoice', // Use actual translated value instead of key
      readonly: true,
      disabled: true,
      section: 'retailStore.form.sections.store_billing',
    },
    {
      label: 'retailStore.form.fields.self_pickup',
      controlName: 'selfPickupFlag',
      type: 'checkbox',
      section: 'retailStore.form.sections.store_billing',
      defaultValue: false,
    },
  ],
  headerActions: [
    {
      label: 'common.actions.cancel',
      action: 'cancel',
      severity: 'secondary',
      outlined: true,
    },
    {
      label: 'common.actions.save',
      action: 'save',
      severity: 'info',
    },
  ],
};

/**
 * Convert defaultValue from i18n key to translated label
 */
export function translateDefaultValues(
  translateService: TranslateService
): FormField[] {
  return RETAIL_STORE_FORM_CONFIG.formFields.map((field) => {
    if (
      field.defaultValue &&
      typeof field.defaultValue === 'string' &&
      field.defaultValue.includes('.')
    ) {
      const translatedValue = translateService.instant(field.defaultValue);
      if (translatedValue !== field.defaultValue) {
        return { ...field, defaultValue: translatedValue };
      }
    }
    return field;
  });
}

/**
 * Convert sales organization tree data to dropdown options
 */
export function convertSalesOrganizationToOptions(
  data: ListOfTreeVO[]
): Array<{ label: string; value: number }> {
  const options: Array<{ label: string; value: number }> = [];

  const processNode = (node: ListOfTreeVO, level: number = 0) => {
    if (node.id && node.orgName) {
      const indent = '  '.repeat(level);
      options.push({
        label: `${indent}${node.orgName}`,
        value: node.id,
      });
    }

    if (node.children) {
      node.children.forEach((child) => {
        processNode(child, level + 1);
      });
    }
  };

  data.forEach((node) => processNode(node));
  return options;
}
